<template>
  <Loading v-if="loading" />
  <div v-else class="course-workspace">
    <div class="ds-container ds-container--wide">
      <div class="course-header">
        <img v-if="course?.image_link" :src="course.image_link" class="course-header__thumb" alt="" />
        <span v-else class="course-header__thumb course-header__thumb--empty"><i class="pi pi-book"></i></span>

        <div class="course-header__meta">
          <h1 class="course-header__title">{{ course?.title }}</h1>
          <div class="course-header__badges">
            <span v-if="course?.academic_year" class="badge-pill">
              <i class="pi pi-graduation-cap"></i> {{ course.academic_year.name }}
            </span>
            <button
              class="chip-toggle" :class="course?.is_visible ? 'chip-toggle--on' : 'chip-toggle--off'"
              v-tooltip.top="course?.is_visible ? 'اضغط لإخفاء الكورس' : 'اضغط لإظهار الكورس'"
              @click="toggleVisible"
            >
              <i :class="course?.is_visible ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
              {{ course?.is_visible ? 'ظاهر' : 'مخفي' }}
            </button>
            <button
              class="chip-toggle" :class="course?.is_subscription_enabled ? 'chip-toggle--on' : 'chip-toggle--off'"
              v-tooltip.top="'تفعيل/تعطيل الاشتراك المدفوع'"
              @click="toggleSubscription"
            >
              <i class="pi pi-lock"></i>
              {{ course?.is_subscription_enabled ? 'اشتراك مفعّل' : 'بدون اشتراك' }}
            </button>
          </div>
        </div>

        <div class="course-header__actions">
          <router-link :to="`/courses/${courseId}`" class="ds-btn ds-btn--secondary ds-btn--sm">
            <i class="pi pi-pencil"></i> تعديل البيانات
          </router-link>
          <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف الكورس'" @click="remove">
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>

      <nav class="course-tabs">
        <router-link
          v-for="tab in tabs"
          :key="tab.key"
          :to="tabTo(tab)"
          class="course-tabs__item"
          :class="{ 'course-tabs__item--active': isActive(tab) }"
        >
          <i :class="`pi ${tab.icon}`"></i>
          <span>{{ tab.label }}</span>
        </router-link>
      </nav>

      <div class="course-workspace__body">
        <router-view @saved="onSaved" />
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import Loading from '@/components/global/loading.vue';
import { useCourseStore } from '@/stores/useCourseStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

const TABS = [
  { key: 'overview', label: 'نظرة عامة', icon: 'pi-th-large', to: '' },
  { key: 'pricing', label: 'الأسعار', icon: 'pi-money-bill', to: 'pricing' },
  { key: 'chapters', label: 'الأبواب', icon: 'pi-sitemap', to: 'chapters' },
  { key: 'lessons', label: 'الدروس', icon: 'pi-book', to: 'lessons' },
  { key: 'contents', label: 'المحتوى', icon: 'pi-cloud-upload', to: 'contents' },
  { key: 'question-bank', label: 'بنك الأسئلة', icon: 'pi-question-circle', to: 'question-bank' },
  { key: 'exams', label: 'الامتحانات', icon: 'pi-file-export', to: 'exams' },
];

export default {
  name: 'CourseLayout',
  components: { Loading },
  data() {
    return {
      loading: true,
      tabs: TABS,
      // Shared reactive context handed down to every tab page via inject('courseCtx').
      courseCtx: {
        courseId: this.$route.params.courseId,
        course: null,
        breadcrumbBase: [],
      },
    };
  },
  provide() {
    return { courseCtx: this.courseCtx };
  },
  computed: {
    store() { return useCourseStore(); },
    courseId() { return this.$route.params.courseId; },
    course() { return this.courseCtx.course; },
    activeSegment() {
      const base = `/courses/${this.courseId}`;
      const rest = this.$route.path.slice(base.length).replace(/^\//, '');
      return rest.split('/')[0] || '';
    },
  },
  watch: {
    courseId: {
      immediate: true,
      handler(id) {
        this.courseCtx.courseId = id;
        this.loadCourse();
      },
    },
  },
  methods: {
    setCourse(course) {
      this.courseCtx.course = course;
      this.courseCtx.breadcrumbBase = [
        { name: 'الرئيسية', path: '/' },
        { name: 'الكورسات', path: '/courses' },
        { name: course?.title || '...', path: `/courses/${this.courseId}` },
      ];
    },
    async loadCourse() {
      this.loading = true;
      try {
        const course = await this.store.fetchCourse(this.courseId);
        this.setCourse(course);
      } finally {
        this.loading = false;
      }
    },
    onSaved(updated) {
      if (updated) this.setCourse(updated);
      else this.loadCourse();
    },
    tabTo(tab) {
      return `/courses/${this.courseId}${tab.to ? '/' + tab.to : ''}`;
    },
    isActive(tab) {
      return this.activeSegment === tab.to;
    },
    async toggleVisible() {
      try { this.setCourse(await this.store.toggleVisible(this.courseId)); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async toggleSubscription() {
      try { this.setCourse(await this.store.toggleSubscription(this.courseId)); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async remove() {
      const res = await Swal.fire({
        title: 'حذف الكورس',
        html: `سيتم حذف <strong>${this.course?.title ?? ''}</strong> نهائياً بكل محتواه.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.deleteCourse(this.courseId);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
        this.$router.push('/courses');
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
};
</script>

<style scoped>
.course-workspace { padding: 2rem 0 4rem; }

.course-header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}
.course-header__thumb {
  width: 72px; height: 72px; border-radius: var(--radius-md); object-fit: cover;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--primary-light); color: var(--primary-color);
  border: 1px solid var(--surface-border); font-size: 1.5rem; flex-shrink: 0;
}
.course-header__meta { display: flex; flex-direction: column; gap: 0.6rem; min-width: 0; flex: 1; }
.course-header__title { font-size: 1.4rem; font-weight: 800; color: var(--text-primary); margin: 0; }
.course-header__badges { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.course-header__actions { display: flex; align-items: center; gap: 0.5rem; margin-inline-start: auto; }

.course-tabs {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  overflow-x: auto;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md);
  padding: 0.4rem;
  margin-bottom: 1.5rem;
  scrollbar-width: thin;
}
.course-tabs__item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
  padding: 0.55rem 0.9rem;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.85rem;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
}
.course-tabs__item:hover { background: var(--surface-hover); color: var(--text-primary); }
.course-tabs__item--active {
  background: var(--primary-color);
  color: #fff;
}
.course-tabs__item--active:hover { background: var(--primary-color); color: #fff; }
</style>
